import { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor,
} from "@/components/ui/combobox"
import { useBooking } from "@/context/BookingContext"
import { cacheCityCode } from "@/lib/airports"
import { usePelikanAutocomplete } from "@/hooks/usePelikanAutocomplete"
import { useAutoDetectAirport } from "@/hooks/useAutoDetectAirport"
import type { AirportOption } from "@/types/booking"

interface AirportComboboxProps {
  mode?: "departure" | "destination"
  error?: boolean
  onErrorClear?: () => void
}

export function AirportCombobox({ mode = "departure", error, onErrorClear }: AirportComboboxProps) {
  const { state, dispatch } = useBooking()
  const isDestination = mode === "departure" ? false : true
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const { suggestions, isLoading } = usePelikanAutocomplete(search)
  const anchor = useComboboxAnchor()

  useAutoDetectAirport(mode === "departure" && state.departureAirports.length === 0)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (error) setOpen(true)
  }, [error])

  const items: AirportOption[] = suggestions.map((s) => ({
    code: s.cityCode,
    city: s.city,
    country: s.country,
  }))

  function handleDepartureValueChange(newValues: AirportOption[] | null) {
    const next = newValues ?? []
    const current = state.departureAirports
    next.forEach((airport) => {
      if (!current.some((a) => a.code === airport.code)) {
        cacheCityCode(airport.code, airport.city, airport.country ?? "")
        dispatch({ type: "ADD_DEPARTURE_AIRPORT", airport })
      }
    })
    current.forEach((a) => {
      if (!next.some((airport) => airport.code === a.code)) {
        dispatch({ type: "REMOVE_DEPARTURE_AIRPORT", code: a.code })
      }
    })
    onErrorClear?.()
  }

  function handleDestinationValueChange(newValues: AirportOption[] | null) {
    const next = newValues ?? []
    if (next.length === 0) {
      dispatch({ type: "CLEAR_DESTINATION_AIRPORT" })
      return
    }
    const selected = next[next.length - 1]
    cacheCityCode(selected.code, selected.city, selected.country ?? "")
    dispatch({ type: "SET_DESTINATION_AIRPORT", airport: selected })
    setOpen(false)
    setSearch("")
    onErrorClear?.()
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    if (!nextOpen) {
      setSearch("")
      onErrorClear?.()
    }
  }

  if (isDestination) {
    const selected = state.destinationAirport
    const chipsValue = selected ? [selected] : []

    return (
      <div className="space-y-1.5">
        <Combobox
          multiple
          items={items}
          value={chipsValue}
          onValueChange={handleDestinationValueChange}
          isItemEqualToValue={(item, value) => item.code === value.code}
          itemToStringLabel={(item) => item.city}
          onInputValueChange={(inputValue) => setSearch(inputValue)}
          open={open}
          onOpenChange={handleOpenChange}
          filter={null}
        >
          <ComboboxChips ref={anchor}>
            {selected && (
              <ComboboxChip key={selected.code}>
                {selected.city}
              </ComboboxChip>
            )}
            <ComboboxChipsInput
              placeholder={selected ? "" : "Select destination city"}
            />
          </ComboboxChips>

          <ComboboxContent anchor={anchor}>
            <ComboboxEmpty>
              {isLoading ? (
                <Loader2 className="mx-auto size-4 animate-spin text-muted-foreground" />
              ) : !search.trim() ? (
                "Type to search for a city..."
              ) : (
                "No cities found"
              )}
            </ComboboxEmpty>
            <ComboboxList>
              {items.map((item) => (
                <ComboboxItem
                  key={item.code}
                  value={item}
                >
                  <div className="flex-1">
                    <div>{item.city}</div>
                    {item.country && (
                      <span className="text-xs text-muted-foreground">
                        {item.country}
                      </span>
                    )}
                  </div>
                </ComboboxItem>
              ))}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </div>
    )
  }

  const atLimit = state.departureAirports.length >= 3

  return (
    <div className="space-y-1.5">
      <Combobox
        multiple
        items={items}
        value={state.departureAirports}
        onValueChange={handleDepartureValueChange}
        isItemEqualToValue={(item, value) => item.code === value.code}
        itemToStringLabel={(item) => item.city}
        onInputValueChange={(inputValue) => setSearch(inputValue)}
        open={open}
        onOpenChange={handleOpenChange}
        filter={null}
      >
        <ComboboxChips ref={anchor}>
          {state.departureAirports.map((airport) => (
            <ComboboxChip
              key={airport.code}
            >
              {airport.city}
            </ComboboxChip>
          ))}
          <ComboboxChipsInput
            placeholder={
              state.departureAirports.length === 0
                ? "Select departure city (max 3)"
                : !atLimit
                  ? "Add another city..."
                  : ""
            }
            disabled={atLimit}
          />
        </ComboboxChips>

        <ComboboxContent anchor={anchor}>
          <ComboboxEmpty>
            {isLoading ? (
              <Loader2 className="mx-auto size-4 animate-spin text-muted-foreground" />
            ) : !search.trim() ? (
              "Type to search for a city..."
            ) : (
              "No cities found"
            )}
          </ComboboxEmpty>
          <ComboboxList>
            {items.map((item) => {
              const itemAtLimit =
                atLimit && !state.departureAirports.some((a) => a.code === item.code)
              return (
                <ComboboxItem
                  key={item.code}
                  value={item}
                  disabled={itemAtLimit}
                >
                  <div className="flex-1">
                    <div>{item.city}</div>
                    {item.country && (
                      <span className="text-xs text-muted-foreground">
                        {item.country}
                      </span>
                    )}
                  </div>
                </ComboboxItem>
              )
            })}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>

      {error && (
        <p className="text-xs font-medium text-destructive">
          Please select a departure airport first
        </p>
      )}
    </div>
  )
}
